import config from '../../../config/config.json';

const getHeaders = (includeContentType = true) => {
  const sessionToken = localStorage.getItem('session_token');
  const headers: Record<string, string> = {};
  if (sessionToken) {
    headers['session-token'] = sessionToken;
  }
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
};

export const fetchAllTeams = async () => {
  const response = await fetch(`${config.backend_base_url}api/teams`, {
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch teams');
  }
  return response.json();
};

export const fetchTeamById = async (id: string) => {
  const response = await fetch(`${config.backend_base_url}api/team/${id}`, {
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch team');
  }
  return response.json();
};

export const fetchTeamBySlug = async (slug: string) => {
  const response = await fetch(`${config.backend_base_url}api/team/slug/${slug}`, {
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch team by slug');
  }
  return response.json();
};

export const createTeam = async (teamData: FormData) => {
  const response = await fetch(`${config.backend_base_url}api/team`, {
    method: 'POST',
    headers: getHeaders(false), // Don't include Content-Type for FormData
    body: teamData,
  });
  if (!response.ok) {
    throw new Error('Failed to create team');
  }
  return await response.json();
};

export const updateTeam = async (id: string, teamData: FormData) => {
  // Create a JSON object from the form data
  const teamDataObject: Record<string, any> = {};

  // Extract the published value as a boolean
  const publishedValue = teamData.get('published');
  if (publishedValue !== null) {
    teamDataObject.published = publishedValue === 'true';
  }

  // Create a new FormData with the correct published value
  const processedData = new FormData();

  // Copy each field from the original FormData
  const fields = ['name', 'description', 'inviteLink', 'rank', 'slug', 'logoUpload'];
  fields.forEach((field) => {
    const value = teamData.get(field);
    if (value !== null) {
      processedData.append(field, value);
    }
  });

  // Add the published field as a JSON string to ensure it's treated as a boolean
  if (publishedValue !== null) {
    processedData.append('published', JSON.stringify(teamDataObject.published));
  }

  // Handle modMembers array
  const modMembersKeys = Array.from(teamData.keys()).filter((key) => key.startsWith('modMembers'));
  modMembersKeys.forEach((key) => {
    const value = teamData.get(key);
    if (value !== null) {
      processedData.append(key, value);
    }
  });

  const response = await fetch(`${config.backend_base_url}api/team/${id}`, {
    method: 'PUT',
    headers: getHeaders(false), // Don't include Content-Type for FormData
    body: processedData,
  });
  if (!response.ok) {
    throw new Error('Failed to update team');
  }
  return response.json();
};

export const deleteTeam = async (id: string) => {
  const response = await fetch(`${config.backend_base_url}api/team/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to delete team');
  }
  return response.json();
};

export const addMemberToTeam = async (teamId: string, userId: string) => {
  const response = await fetch(`${config.backend_base_url}api/team/${teamId}/members`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ userId }),
  });
  if (!response.ok) {
    throw new Error('Failed to add member');
  }
  return response.json();
};

export const removeMemberFromTeam = async (teamId: string, userId: string) => {
  const response = await fetch(`${config.backend_base_url}api/team/${teamId}/members/${userId}`, {
    method: 'DELETE',
    headers: getHeaders(),
    body: JSON.stringify({
      team_id: parseInt(teamId, 10),
      user_id: parseInt(userId, 10),
    }),
  });

  if (!response.ok) {
    const _errorText = await response.text();
    throw new Error(`Failed to remove member: ${response.status} ${response.statusText}`);
  }

  // Check if there's content before trying to parse JSON
  const text = await response.text();
  if (!text || text.trim() === '') {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    // Return empty object if JSON parsing fails
    return {};
  }
};

export const fetchAllUsers = async () => {
  const response = await fetch(`${config.backend_base_url}staff/users`, {
    headers: getHeaders(),
  });
  if (!response.ok) {
    throw new Error('Failed to fetch users');
  }
  return response.json();
};

export const addTeamMember = async (teamId: string, userId: string) => {
  const response = await fetch(`${config.backend_base_url}api/team/${teamId}/members`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ user_id: userId, team_id: teamId }),
  });

  if (!response.ok) {
    throw new Error('Failed to add team member');
  }

  return response.json();
};

export const fetchUserTeams = async (userId: string): Promise<any[]> => {
  const response = await fetch(`${config.backend_base_url}api/user/${userId}/teams`, {
    method: 'GET',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch teams for user ${userId}`);
  }

  return response.json();
};
